// CAMINHO: src/components/cases/EditAdministrativeProcessModal.tsx
// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from 'lucide-react';
import { AdministrativeFinancialIntegrationService } from "@/services/administrativeFinancialIntegrationService";
import AdvancedFeeConfiguration, { FeeConfiguration } from '@/components/financial/AdvancedFeeConfiguration';

interface AdministrativeProcess {
  id: string;
  title: string;
  description?: string;
  procedure_type?: string;
  administrative_agency?: string;
  process_number?: string;
  priority: string;
  status: string;
  client_fees?: string;
  court_costs?: string;
  success_fee_type?: string;
  success_fee_value?: string;
  procedural_strategy?: string;
  internal_notes?: string;
  fee_configuration?: any;
}

interface EditAdministrativeProcessModalProps {
  processDetails: AdministrativeProcess;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess: () => void;
}

export const EditAdministrativeProcessModal = ({ processDetails, isOpen, onOpenChange, onSuccess }: EditAdministrativeProcessModalProps) => {
  const [editableData, setEditableData] = useState<Partial<AdministrativeProcess>>({});
  const [feeConfiguration, setFeeConfiguration] = useState<Partial<FeeConfiguration>>({});
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setEditableData(processDetails);
      // Carregar configuração de honorários se existir
      if (processDetails.fee_configuration) {
        setFeeConfiguration(processDetails.fee_configuration);
      } else {
        // Configuração padrão
        setFeeConfiguration({
          benefitValue: 0,
          benefitValueType: 'estimated',
          benefitValueNotes: '',
          benefitPaymentType: 'installments',
          benefitInstallmentCount: 1,
          benefitInstallmentFrequency: 'mensal',
          benefitFirstPaymentDate: '',
          benefitFirstPaymentDatePending: true,
          benefitInstallmentValue: 0,
          hasInitialFee: false,
          initialFeeAmount: 0,
          initialFeeType: 'fixed',
          initialFeeDeductFromFuture: false,
          initialFeePaymentStatus: 'pending',
          initialFeePaymentDate: '',
          initialFeeFuturePaymentDate: '',
          hasSuccessFee: true,
          successFeeType: 'percentage_global',
          successFeeValue: 30,
          successFeeTrigger: 'recebimento',
          successFeeConditions: '',
          successFeeCalculationBase: 'total_benefit',
          paymentMethod: 'a_vista',
          hasDownPayment: false,
          downPaymentAmount: 0,
          downPaymentType: 'fixed',
          installmentCount: 1,
          installmentFrequency: 'mensal',
          latePaymentInterest: 0,
          earlyPaymentDiscount: 0,
          gracePeriodDays: 0,
          paymentConditions: '',
          collectionNotes: ''
        });
      }
    }
  }, [processDetails, isOpen]);

  const handleInputChange = (field: keyof AdministrativeProcess, value: any) => {
    setEditableData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Incluir configuração de honorários nos dados a serem salvos
      // Preserva os honorários configurados na aba Financeiro do processo (formulário simples)
      const savedFee = (processDetails as any).fee_configuration || {};
      const usesSimpleContract = !!savedFee.simpleContract || !!savedFee.isProBono;
      const { clients: _clients, ...plainData } = editableData as any;
      const dataToUpdate = {
        ...plainData,
        fee_configuration: usesSimpleContract
          ? savedFee
          : { ...feeConfiguration, simpleContract: savedFee.simpleContract, isProBono: savedFee.isProBono },
      };

      const { error } = await supabase
        .from('administrative_processes')
        .update(dataToUpdate)
        .eq('id', processDetails.id);

      if (error) throw error;

      // Verificar se há configuração de benefícios e se a data foi definida
      const hasBenefitConfig = feeConfiguration.benefitValue && feeConfiguration.benefitValue > 0;
      const wasDatePending = processDetails.fee_configuration?.benefitFirstPaymentDatePending !== false;
      const isDateNowDefined = feeConfiguration.benefitFirstPaymentDate && !feeConfiguration.benefitFirstPaymentDatePending;
      
      // Se há configuração de benefícios e a data foi definida agora, iniciar procedimentos automáticos
      if (usesSimpleContract) {
        // Lançamentos são gerados pela aba Financeiro do processo; nada a refazer aqui
      } else if (hasBenefitConfig && wasDatePending && isDateNowDefined) {
        try {
          // Criar/atualizar integração financeira automática
          await AdministrativeFinancialIntegrationService.updateFinancialIntegration(processDetails.id);
          
          toast({
            title: "Procedimentos Automáticos Iniciados",
            description: "A integração financeira foi atualizada automaticamente com base na data informada.",
          });
        } catch (integrationError) {
          console.error('Erro na integração financeira automática:', integrationError);
          toast({
            title: "Aviso",
            description: "Processo salvo, mas houve um problema na integração financeira automática.",
            variant: "destructive",
          });
        }
      } else if (hasBenefitConfig && isDateNowDefined) {
        // Se a data já estava definida, apenas atualizar a integração
        try {
          await AdministrativeFinancialIntegrationService.updateFinancialIntegration(processDetails.id);
        } catch (integrationError) {
          console.error('Erro na integração financeira:', integrationError);
        }
      }

      toast({ title: "Sucesso!", description: "As informações do processo administrativo foram atualizadas." });
      onSuccess();
      onOpenChange(false);

    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const LOGO = '/imgs/logos allana/letra_A_logo.png';
  const label = 'block text-sm font-medium text-slate-700';
  const input = 'w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30';
  const tabTrigger = 'rounded-lg px-3 py-2 text-[13px] text-slate-500 transition-colors data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-[#7a6440] data-[state=active]:shadow-sm';
  const sectionTitle = 'text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9a7f52] md:col-span-2';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
        <DialogHeader className="relative -m-6 mb-2 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          <img src={LOGO} alt="" aria-hidden="true" className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]" />
          <div className="relative flex items-center gap-4 text-left">
            <img src={LOGO} alt="Allana Sthéfany" className="h-12 w-12 shrink-0 select-none object-contain opacity-80" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Processo administrativo</p>
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">Editar processo</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">Ajuste os dados deste processo administrativo.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="pt-4">
          <Tabs defaultValue="basic" className="w-full">
            <TabsContent value="basic" className="mt-1">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <p className={sectionTitle}>Dados do processo</p>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="title" className={label}>Título</Label>
                  <Input id="title" value={editableData.title || ''} onChange={(e) => handleInputChange('title', e.target.value)} className={input} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="procedure_type" className={label}>Tipo de procedimento</Label>
                  <Input id="procedure_type" value={editableData.procedure_type || ''} onChange={(e) => handleInputChange('procedure_type', e.target.value)} placeholder="Ex.: Salário-maternidade" className={input} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="administrative_agency" className={label}>Órgão</Label>
                  <Input id="administrative_agency" value={editableData.administrative_agency || ''} onChange={(e) => handleInputChange('administrative_agency', e.target.value)} placeholder="Ex.: INSS" className={input} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="process_number" className={label}>Número do processo / protocolo</Label>
                  <Input id="process_number" value={editableData.process_number || ''} onChange={(e) => handleInputChange('process_number', e.target.value)} className={input} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status" className={label}>Status</Label>
                  <Select onValueChange={(value) => handleInputChange('status', value)} value={editableData.status}>
                    <SelectTrigger id="status" className={input}>
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="em_andamento">Em andamento</SelectItem>
                      <SelectItem value="aguardando_documentos">Aguardando documentos</SelectItem>
                      <SelectItem value="aguardando_resposta">Aguardando resposta</SelectItem>
                      <SelectItem value="concluido">Concluído</SelectItem>
                      <SelectItem value="arquivado">Arquivado</SelectItem>
                      <SelectItem value="suspenso">Suspenso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority" className={label}>Prioridade</Label>
                  <Select onValueChange={(value) => handleInputChange('priority', value)} value={editableData.priority}>
                    <SelectTrigger id="priority" className={input}>
                      <SelectValue placeholder="Selecione a prioridade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixa">Baixa</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description" className={label}>Descrição</Label>
                  <Textarea id="description" value={editableData.description || ''} onChange={(e) => handleInputChange('description', e.target.value)} rows={2} className={`${input} resize-none`} />
                </div>

                <p className={`${sectionTitle} mt-2`}>Valores</p>
                <div className="space-y-2">
                  <Label htmlFor="client_fees" className={label}>Honorários do cliente</Label>
                  <Input id="client_fees" value={editableData.client_fees || ''} onChange={(e) => handleInputChange('client_fees', e.target.value)} placeholder="R$ 0,00" className={input} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="court_costs" className={label}>Custas</Label>
                  <Input id="court_costs" value={editableData.court_costs || ''} onChange={(e) => handleInputChange('court_costs', e.target.value)} placeholder="R$ 0,00" className={input} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="success_fee_type" className={label}>Honorário de êxito</Label>
                  <Select onValueChange={(value) => handleInputChange('success_fee_type', value)} value={editableData.success_fee_type}>
                    <SelectTrigger id="success_fee_type" className={input}>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Porcentagem</SelectItem>
                      <SelectItem value="fixed">Valor fixo</SelectItem>
                      <SelectItem value="conditional">Condicional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="success_fee_value" className={label}>Valor do êxito</Label>
                  <Input
                    id="success_fee_value"
                    value={editableData.success_fee_value || ''}
                    onChange={(e) => handleInputChange('success_fee_value', e.target.value)}
                    placeholder={editableData.success_fee_type === 'percentage' ? '% (ex.: 30)' : 'R$ 0,00'}
                    className={input}
                  />
                </div>

                <p className={`${sectionTitle} mt-2`}>Anotações</p>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="procedural_strategy" className={label}>Estratégia</Label>
                  <Textarea id="procedural_strategy" value={editableData.procedural_strategy || ''} onChange={(e) => handleInputChange('procedural_strategy', e.target.value)} rows={2} className={`${input} resize-none`} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="internal_notes" className={label}>Observações internas</Label>
                  <Textarea id="internal_notes" value={editableData.internal_notes || ''} onChange={(e) => handleInputChange('internal_notes', e.target.value)} rows={2} className={`${input} resize-none`} />
                </div>
              </div>
            </TabsContent>

          </Tabs>

          <p className="mt-5 rounded-xl bg-[#faf7f2] px-4 py-3 text-xs text-slate-600">
            Honorários e parcelas ficam na aba <strong className="text-slate-800">Financeiro</strong> da página do processo, com resumo, "Alterar" e "Marcar como pago".
          </p>

          <DialogFooter className="mt-6 flex flex-col gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end sm:gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancelar</Button>
            <Button type="submit" disabled={loading} className="rounded-xl bg-[#c4ab83] px-6 font-semibold text-slate-900 hover:bg-[#b39a72]">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar alterações
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};